import React, { useState } from 'react';
import { Grid3X3, Settings, Shield, User, LogOut } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useWorkspace } from '../context/WorkspaceContext';
import { Popover, PopoverTrigger, PopoverContent } from '../components/ui/popover';
import './WorkspaceRail.css';

const MAX_VISIBLE_TILES = 8;

export default function WorkspaceRail({ onOpenHub, getHealthStatus }) {
    const { user, logout } = useAuth();
    const { currentWorkspace, setCurrentWorkspace, visibleWorkspaces, isWorkspaceActive } = useWorkspace();
    const [popoverOpen, setPopoverOpen] = useState(false);

    // Filter out virtual workspaces (settings, admin) — they are accessed via
    // the dedicated Settings gear and Shield buttons below, not as rail tiles.
    const railWorkspaces = visibleWorkspaces
        .filter(ws => isWorkspaceActive(ws) && ws.id !== 'settings' && ws.id !== 'admin')
        .slice(0, MAX_VISIBLE_TILES);

    const initials = (user?.name || '')
        .split(' ')
        .filter(Boolean)
        .map(p => p[0])
        .slice(0, 2)
        .join('')
        .toUpperCase();

    const isSettingsActive = currentWorkspace.id === 'settings';
    const isAdminActive = currentWorkspace.id === 'admin';

    const handleProfile = () => {
        setPopoverOpen(false);
        setCurrentWorkspace('settings'); // auto-navigates to /settings/profile
    };

    const handleSignOut = () => {
        setPopoverOpen(false);
        logout();
    };

    return (
        <nav className="ws-rail" aria-label="Workspaces">
            <div
                className="ws-rail-logo"
                title={user?.org_name || 'Workspace'}
                aria-hidden="true"
            >
                {(user?.org_name || 'N').charAt(0).toUpperCase()}
            </div>

            <div className="ws-rail-stack">
                {railWorkspaces.map(ws => {
                    const Glyph = ws.glyph;
                    const health = ws.providerId && typeof getHealthStatus === 'function'
                        ? getHealthStatus(ws.providerId)
                        : 'unknown';
                    const showBadge = health === 'degraded' || health === 'down';
                    const isCurrent = ws.id === currentWorkspace.id;
                    return (
                        <button
                            key={ws.id}
                            type="button"
                            className={`ws-tile ${isCurrent ? 'on' : ''}`}
                            style={{ '--h': ws.hue }}
                            onClick={() => setCurrentWorkspace(ws.id)}
                            data-testid={`workspace-rail-tile-${ws.id}`}
                            aria-label={ws.name}
                            aria-current={isCurrent ? 'page' : undefined}
                        >
                            <Glyph size={18} strokeWidth={2} aria-hidden="true" />
                            {showBadge && <span className={`ws-tile-dot ${health}`} aria-hidden="true" />}
                            <span className="ws-tile-tip">
                                {ws.name}{showBadge ? ` · ${health}` : ''}
                            </span>
                        </button>
                    );
                })}
            </div>

            <div className="ws-rail-sep" aria-hidden="true" />

            <button
                type="button"
                className="ws-railbtn"
                onClick={onOpenHub}
                data-testid="workspace-hub-btn"
                aria-label="Open Workspace Hub"
            >
                <Grid3X3 size={18} strokeWidth={2} aria-hidden="true" />
                <span className="ws-tile-tip">Workspace Hub</span>
            </button>

            <div className="ws-rail-flex" />

            {/* Settings — gear icon, switches to Settings virtual workspace */}
            <button
                type="button"
                className={`ws-railbtn ${isSettingsActive ? 'on' : ''}`}
                onClick={() => setCurrentWorkspace('settings')}
                aria-label="Settings"
                aria-current={isSettingsActive ? 'page' : undefined}
                data-testid="workspace-rail-settings"
            >
                <Settings size={17} strokeWidth={2} aria-hidden="true" />
                <span className="ws-tile-tip">Settings</span>
            </button>

            {/* Admin — shield icon, admin-gated */}
            {user?.is_admin && (
                <button
                    type="button"
                    className={`ws-railbtn ${isAdminActive ? 'on' : ''}`}
                    onClick={() => setCurrentWorkspace('admin')}
                    aria-label="Admin"
                    aria-current={isAdminActive ? 'page' : undefined}
                    data-testid="workspace-rail-admin"
                >
                    <Shield size={17} strokeWidth={2} aria-hidden="true" />
                    <span className="ws-tile-tip">Admin</span>
                </button>
            )}

            {/* Avatar — opens popover with Profile + Sign Out */}
            <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
                <PopoverTrigger asChild>
                    <button
                        type="button"
                        className="ws-rail-avatar"
                        aria-label={user?.name || 'Profile'}
                        title={user?.name || 'Profile'}
                    >
                        {user?.picture ? (
                            <img src={user.picture} alt="" />
                        ) : (
                            <span>{initials || 'U'}</span>
                        )}
                    </button>
                </PopoverTrigger>
                <PopoverContent side="right" align="end" sideOffset={8} className="ws-avatar-popover">
                    <div className="ws-avatar-popover-header">
                        <div className="ws-avatar-popover-avatar">
                            {user?.picture ? (
                                <img src={user.picture} alt={user.name} />
                            ) : (
                                <span>{initials || 'U'}</span>
                            )}
                        </div>
                        <div className="ws-avatar-popover-info">
                            <p className="ws-avatar-popover-name">{user?.name}</p>
                            <p className="ws-avatar-popover-email">{user?.email}</p>
                        </div>
                    </div>
                    <div className="ws-avatar-popover-sep" />
                    <button type="button" className="ws-avatar-popover-item" onClick={handleProfile}>
                        <User size={16} /><span>Profile</span>
                    </button>
                    <button type="button" className="ws-avatar-popover-item ws-avatar-popover-danger" onClick={handleSignOut}>
                        <LogOut size={16} /><span>Sign Out</span>
                    </button>
                </PopoverContent>
            </Popover>
        </nav>
    );
}
